import { useCallback } from 'react';
import { useAngularContext } from 'AngularContext';
import { useCurrentUser, useEventLogger, useNavigationHelper } from 'FrontRoyalAngular';
import { useErrorLogService } from 'ErrorLogging';
import { claimExamVerification, mustClaimExamVerificationToLaunchStream } from 'ExamVerification';
import { lessonLaunchPath, type StreamForStreamDashboard, type LessonForStreamDashboard } from 'Lessons';
import { type AnyObject } from '@Types';
import { getChapterIndexForLesson, getStreamLogInfo, isLessonComplete } from '../selectors';
import { toStreamForDashboard } from '../toStreamForDashboard';

/*
    Logs lesson:clicked_launch, starts the launch timer, claims any outstanding exam verification, then
    navigates. claimExamVerification uses fetch, so check response.ok. A catch does not see error responses.
*/
export function useLaunchLesson(stream: StreamForStreamDashboard) {
    const currentUser = useCurrentUser();
    const $injector = useAngularContext();
    const EventLogger = useEventLogger();
    const { loadRoute } = useNavigationHelper();
    const ErrorLogService = useErrorLogService();

    return useCallback(
        (lesson: LessonForStreamDashboard, linkId: string) => {
            const label = (linkId || 'unknown').toLowerCase().replace(' ', '_');
            if (!linkId) ErrorLogService.notify(new Error('No linkId provided when launching lesson'));

            const launch = () => {
                const timerSingleton = $injector.get<AnyObject<(name: string) => void>>('timerSingleton');

                EventLogger.log('lesson:clicked_launch', { ...getLessonLogInfo(stream, lesson), label });
                timerSingleton.startTimer(`lesson:clickedLaunch:${lesson.id}`);

                loadRoute(
                    lessonLaunchPath({
                        lessonId: lesson.id,
                        streamId: stream.id,
                        chapterIndex: getChapterIndexForLesson(stream, lesson) ?? 0,
                    }),
                );
            };

            const streamForDashboard = toStreamForDashboard(stream);
            if (mustClaimExamVerificationToLaunchStream(currentUser ?? undefined, streamForDashboard)) {
                claimExamVerification(currentUser!, streamForDashboard).then(response => {
                    if (response.ok) launch();
                });
                return;
            }

            launch();
        },
        [stream, currentUser, $injector, EventLogger, loadRoute, ErrorLogService],
    );
}

/*
    Mirrors Lesson#logInfo, which merged the stream's log info into the lesson's. The keys stay
    snake-cased, because they land in the events table and in Amplitude under these names.
*/
function getLessonLogInfo(stream: StreamForStreamDashboard, lesson: LessonForStreamDashboard): AnyObject {
    return {
        lesson_id: lesson.id,
        lesson_complete: isLessonComplete(lesson),
        lesson_title: lesson.title,
        lesson_version_id: lesson.versionId,
        ...getStreamLogInfo(stream),
    };
}
